import { LitElement } from "lit";
import { customElement, property } from "lit/decorators.js";
import { onMarketingAllowed } from "../utils/consent";

/**
 * Bootstrap component that injects marketing/tracking vendor scripts (Hovr,
 * Gobot) only once the visitor has consented, per Shopify Customer Privacy.
 * Audit finding this fixes: both vendors kept transmitting after opt-out
 * because they were hard-coded `<script>` tags with no consent check.
 *
 * NoFraud is deliberately not gated here — it's fraud prevention, not
 * marketing, and isn't present in this theme's codebase.
 *
 * Usage (place once in layout/theme.liquid, inside <head> or <body>):
 * <consent-gated-scripts
 *   data-hovr-enabled="true"
 *   data-hovr-shop-id="{{ settings.hovr_shop_id }}"
 *   data-gobot-key="{{ settings.gobot_key }}"
 * ></consent-gated-scripts>
 */
@customElement("consent-gated-scripts")
export class ConsentGatedScripts extends LitElement {
  @property({ type: String, attribute: "data-hovr-enabled" })
  hovrEnabled: string | boolean = false;

  @property({ type: String, attribute: "data-hovr-shop-id" })
  hovrShopId = "";

  @property({ type: String, attribute: "data-gobot-key" })
  gobotKey = "";

  connectedCallback(): void {
    super.connectedCallback();

    const hovrEnabled =
      this.hovrEnabled === true || this.hovrEnabled === "true";

    if (hovrEnabled && this.hovrShopId) {
      onMarketingAllowed(() => this._loadHovr(this.hovrShopId));
    }

    if (this.gobotKey) {
      onMarketingAllowed(() => this._loadGobot(this.gobotKey));
    }
  }

  private _loadHovr(shopId: string): void {
    if (document.getElementById("hio-script-identifier")) return;
    const script = document.createElement("script");
    script.id = "hio-script-identifier";
    script.defer = true;
    script.src = "https://script.gethovr.com/bundle.js";
    script.dataset.id = shopId;
    document.head.appendChild(script);
  }

  private _loadGobot(key: string): void {
    if (document.getElementById("gobot-script")) return;
    const script = document.createElement("script");
    script.id = "gobot-script";
    script.src = `https://track.getgobot.com/gobot/js/shopify.js?key=${encodeURIComponent(key)}`;
    document.head.appendChild(script);
  }

  protected createRenderRoot(): HTMLElement | DocumentFragment {
    return this;
  }
}
